import { useCallback, useEffect, useRef, useState } from 'react';

import VideoUploadClient from '../clients/video-upload-client';

type UseVideoUploadClientProps = {
  videoUploadUrl: string | null;
  isUploading: boolean;
};

const useVideoUploadClient = ({
  videoUploadUrl,
  isUploading,
}: UseVideoUploadClientProps) => {
  const [videoUploadClient, setVideoUploadClient] =
    useState<VideoUploadClient | null>(null);
  const [isReadyToUpload, setIsReadyToUpload] = useState(false);

  useEffect(() => {
    if (!videoUploadUrl) {
      return;
    }

    const videoUploadClient = new VideoUploadClient(videoUploadUrl, {});
    setVideoUploadClient(videoUploadClient);
    setIsReadyToUpload(true);

    return () => {
      // Try to finish the video upload asynchronously before destroying the client
      videoUploadClient
        .finish()
        .catch((err) => {
          console.error('Failed to finish video upload:', err);
        })
        .finally(() => videoUploadClient.destroy());
    };
  }, [videoUploadUrl]);

  useEffect(() => {
    if (!videoUploadClient) {
      return;
    }

    if (isUploading) {
      videoUploadClient.resume();
    } else {
      videoUploadClient.pause();
    }
  }, [videoUploadClient, isUploading]);

  const videoUploadClientRef = useRef<VideoUploadClient | null>(null);
  useEffect(() => {
    videoUploadClientRef.current = videoUploadClient;
  }, [videoUploadClient]);

  const queueChunk = useCallback(async (chunk: Readonly<Blob>) => {
    const currentVideoUploadClient = videoUploadClientRef.current;
    if (!currentVideoUploadClient) {
      console.error('No video upload client found, cannot queue chunk');
      return;
    }

    return currentVideoUploadClient.queueChunk(chunk);
  }, []);

  const finishUpload = useCallback(() => {
    const currentVideoUploadClient = videoUploadClientRef.current;
    if (!currentVideoUploadClient) {
      return;
    }

    return currentVideoUploadClient.finish();
  }, []);

  return {
    queueChunk,
    finishUpload,
    isReadyToUpload,
  };
};

export default useVideoUploadClient;
